import { z } from 'zod';
import { sortParam } from '../sort.js';
import { nullableDate } from '../common.js';
import { TAX_RATES } from '../offers/calc.js';
/**
 * Lager und Inventar (KON-166, siehe docs/lager.md). Zwei Teile: das **Lager** führt Bestände von Waren aus den Produkten, an Lagerorten und in Chargen, ausschließlich über
 * unveränderliche Bewegungen (Wareneingang, Warenausgang, Umlagerung, Korrektur, Inventur). Das **Inventar** führt Betriebsmittel (Geräte, Werkzeuge, Ausstattung) mit
 * Inventarnummer, Standort und Verantwortlichem. Mengen sind Tausendstel der Einheit des Produkts (wie bei Aufträgen), Beträge ganze Cent.
 */
// ---------------------------------------------------------------------------
// Lagerorte
// ---------------------------------------------------------------------------
export const LOCATION_KINDS = ['warehouse', 'room', 'shelf', 'vehicle', 'other'];
export const LOCATION_KIND_LABELS = { warehouse: 'Lager', room: 'Raum', shelf: 'Regal oder Fach', vehicle: 'Fahrzeug', other: 'Sonstiges' };
/** Höchstens drei Ebenen: Lager, Raum oder Regal, Fach */
export const LOCATION_MAX_LEVEL = 3;
const text = (max) => z.string().trim().max(max).nullable().optional().transform((v) => (v ? v : null));
const patchText = (max) => z.string().trim().max(max).nullable().optional().transform((v) => (v === undefined ? undefined : v ? v : null));
const milli = (label) => z.number(`Bitte ${label} angeben`).int('Höchstens drei Nachkommastellen').max(1_000_000_000_000, 'Menge zu groß');
export const createLocationInput = z
    .object({
    parentId: z.uuid('Ungültiger Lagerort').nullable().optional(),
    code: z.string().trim().max(20, 'Höchstens 20 Zeichen').nullable().optional().transform((v) => (v ? v : null)),
    name: z.string().trim().min(1, 'Name fehlt').max(100),
    kind: z.enum(LOCATION_KINDS, 'Bitte eine Art wählen').default('warehouse'),
    note: text(500),
})
    .strict();
export const updateLocationInput = z
    .object({
    parentId: z.uuid('Ungültiger Lagerort').nullable().optional(),
    code: z.string().trim().max(20, 'Höchstens 20 Zeichen').nullable().optional().transform((v) => (v === undefined ? undefined : v ? v : null)),
    name: z.string().trim().min(1, 'Name fehlt').max(100).optional(),
    kind: z.enum(LOCATION_KINDS).optional(),
    note: patchText(500),
    active: z.boolean().optional(),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
// ---------------------------------------------------------------------------
// Bestände
// ---------------------------------------------------------------------------
export const STOCK_STATUSES = ['ok', 'low', 'empty'];
export const STOCK_STATUS_LABELS = { ok: 'Vorrätig', low: 'Unter Mindestbestand', empty: 'Leer' };
/** Lagerführung eines Produkts ein- oder ausschalten und einstellen */
export const stockSettingsInput = z
    .object({
    tracked: z.boolean(),
    minStockMilli: milli('den Mindestbestand').min(0, 'Nicht negativ').default(0),
    batchTracking: z.boolean().default(false),
    defaultLocationId: z.uuid('Ungültiger Lagerort').nullable().optional().transform((v) => v ?? null),
})
    .strict();
export const listStockQuery = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(25),
    q: z.string().trim().max(100).optional(),
    status: z.enum(STOCK_STATUSES).optional(),
    /** Nur Artikel mit Bestand an diesem Ort (und seinen Unterorten) */
    locationId: z.uuid().optional(),
    sort: sortParam(['name', 'sku', 'quantity', 'value', 'revenue'], 'name').optional(),
});
// ---------------------------------------------------------------------------
// Bewegungen
// ---------------------------------------------------------------------------
export const MOVEMENT_KINDS = ['receipt', 'issue', 'transfer_out', 'transfer_in', 'adjustment', 'count', 'reversal'];
export const MOVEMENT_KIND_LABELS = {
    receipt: 'Wareneingang',
    issue: 'Warenausgang',
    transfer_out: 'Umlagerung (ab)',
    transfer_in: 'Umlagerung (an)',
    adjustment: 'Korrektur',
    count: 'Inventur',
    reversal: 'Stornobuchung',
};
/** Bewegungen, die man selbst bucht (die übrigen entstehen aus Umlagerung, Inventur und Storno) */
export const BOOKABLE_KINDS = ['receipt', 'issue', 'adjustment'];
/**
 * Eine Buchung von Hand. Wareneingang und Warenausgang tragen eine positive Menge, die Richtung steht in der Art. Eine Korrektur trägt die Änderung mit Vorzeichen.
 * Bei Waren mit Chargenführung braucht der Wareneingang eine Chargennummer. Der Warenausgang nimmt, wenn keine Charge genannt ist, zuerst die Charge, die als erste abläuft.
 */
export const bookMovementInput = z
    .object({
    kind: z.enum(BOOKABLE_KINDS, 'Bitte die Art der Buchung wählen'),
    productId: z.uuid('Bitte einen Artikel wählen'),
    locationId: z.uuid('Bitte einen Lagerort wählen'),
    quantityMilli: milli('die Menge').min(-1_000_000_000_000),
    batchId: z.uuid('Ungültige Charge').nullable().optional(),
    batchNumber: z.string().trim().min(1).max(50).nullable().optional().transform((v) => (v ? v : null)),
    bestBefore: nullableDate,
    orderId: z.uuid('Ungültiger Auftrag').nullable().optional(),
    referenceText: text(200),
    note: text(500),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.quantityMilli === 0)
        ctx.addIssue({ code: 'custom', path: ['quantityMilli'], message: 'Die Menge darf nicht 0 sein' });
    if (v.kind !== 'adjustment' && v.quantityMilli < 0)
        ctx.addIssue({ code: 'custom', path: ['quantityMilli'], message: 'Bitte eine positive Menge angeben, die Richtung ergibt sich aus der Art' });
    if (v.kind === 'adjustment' && !v.note)
        ctx.addIssue({ code: 'custom', path: ['note'], message: 'Bitte einen Grund für die Korrektur angeben' });
});
export const transferInput = z
    .object({
    productId: z.uuid('Bitte einen Artikel wählen'),
    fromLocationId: z.uuid('Bitte den Lagerort wählen, aus dem umgelagert wird'),
    toLocationId: z.uuid('Bitte den Lagerort wählen, in den umgelagert wird'),
    quantityMilli: milli('die Menge').min(1, 'Die Menge muss größer als 0 sein'),
    batchId: z.uuid('Ungültige Charge').nullable().optional(),
    note: text(500),
})
    .strict()
    .refine((v) => v.fromLocationId !== v.toLocationId, { path: ['toLocationId'], message: 'Von und nach sind derselbe Lagerort' });
export const reverseMovementInput = z.object({ reason: z.string().trim().min(1, 'Bitte einen Grund angeben').max(500) }).strict();
export const listMovementsQuery = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(25),
    productId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    orderId: z.uuid().optional(),
    kind: z.enum(MOVEMENT_KINDS).optional(),
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
    q: z.string().trim().max(100).optional(),
    sort: sortParam(['createdAt', 'product', 'quantity'], '-createdAt').optional(),
});
// ---------------------------------------------------------------------------
// Inventur
// ---------------------------------------------------------------------------
export const COUNT_STATUSES = ['open', 'closed', 'cancelled'];
export const COUNT_STATUS_LABELS = { open: 'Offen', closed: 'Abgeschlossen', cancelled: 'Abgebrochen' };
/** Neue Inventur für einen Lagerort (mit allen Unterorten) oder für alle Lagerorte */
export const createCountInput = z
    .object({
    locationId: z.uuid('Ungültiger Lagerort').nullable().optional().transform((v) => v ?? null),
    name: z.string().trim().max(100).nullable().optional().transform((v) => (v ? v : null)),
})
    .strict();
export const saveCountLinesInput = z
    .object({ lines: z.array(z.object({ id: z.uuid('Ungültige Zeile'), countedMilli: milli('die gezählte Menge').min(0, 'Nicht negativ').nullable() }).strict()).min(1).max(1000) })
    .strict();
export const addCountLineInput = z
    .object({ productId: z.uuid('Bitte einen Artikel wählen'), locationId: z.uuid('Bitte einen Lagerort wählen'), batchId: z.uuid('Ungültige Charge').nullable().optional() })
    .strict();
// ---------------------------------------------------------------------------
// Inventar (Betriebsmittel)
// ---------------------------------------------------------------------------
export const ASSET_STATUSES = ['active', 'repair', 'stored', 'retired'];
export const ASSET_STATUS_LABELS = { active: 'Im Einsatz', repair: 'In Reparatur', stored: 'Eingelagert', retired: 'Ausgemustert' };
const assetFields = {
    name: z.string().trim().min(1, 'Bezeichnung fehlt').max(150),
    category: text(60),
    serialNumber: text(80),
    manufacturer: text(100),
    model: text(100),
    acquiredOn: nullableDate,
    /** Bruttobetrag der Anschaffung in Cent, dazu der Steuersatz. Netto und Steuer rechnet Wirkia. */
    acquisitionGrossCents: z.number('Bitte einen Betrag angeben').int('Betrag in Cent').min(0, 'Nicht negativ').max(1_000_000_000_00).nullable().optional(),
    taxRate: z.union(TAX_RATES.map((r) => z.literal(r)), 'Steuersatz 19, 7 oder 0 %').nullable().optional(),
    /** Nutzungsdauer in Jahren für die lineare Abschreibung */
    depreciationYears: z.number('Bitte die Jahre angeben').int('Ganze Jahre').min(1, 'Mindestens 1 Jahr').max(50, 'Höchstens 50 Jahre').nullable().optional(),
    warrantyUntil: nullableDate,
    locationId: z.uuid('Ungültiger Lagerort').nullable().optional(),
    employeeId: z.uuid('Ungültiger Mitarbeiter').nullable().optional(),
    status: z.enum(ASSET_STATUSES).default('active'),
    note: text(2000),
};
export const createAssetInput = z
    .object(assetFields)
    .strict()
    .superRefine((v, ctx) => {
    if (v.acquisitionGrossCents != null && v.taxRate == null)
        ctx.addIssue({ code: 'custom', path: ['taxRate'], message: 'Bitte den Steuersatz wählen' });
});
export const updateAssetInput = z
    .object({
    name: assetFields.name.optional(),
    category: patchText(60),
    serialNumber: patchText(80),
    manufacturer: patchText(100),
    model: patchText(100),
    acquiredOn: nullableDate,
    acquisitionGrossCents: assetFields.acquisitionGrossCents,
    taxRate: assetFields.taxRate,
    depreciationYears: assetFields.depreciationYears,
    warrantyUntil: nullableDate,
    locationId: assetFields.locationId,
    employeeId: assetFields.employeeId,
    status: z.enum(ASSET_STATUSES).optional(),
    note: patchText(2000),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
export const retireAssetInput = z.object({ reason: z.string().trim().min(1, 'Bitte einen Grund angeben').max(500) }).strict();
export const listAssetsQuery = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(25),
    q: z.string().trim().max(100).optional(),
    status: z.enum(ASSET_STATUSES).optional(),
    locationId: z.uuid().optional(),
    employeeId: z.uuid().optional(),
    category: z.string().trim().max(60).optional(),
    /** Garantie läuft in den nächsten 60 Tagen ab oder ist abgelaufen */
    warranty: z.enum(['expiring', 'expired']).optional(),
    sort: sortParam(['number', 'name', 'status', 'acquiredOn', 'category'], 'number').optional(),
});
/** Menge für die Anzeige: 2,5 oder 1.250 (ohne überflüssige Nullen) */
export const formatQuantityMilli = (milli) => new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 }).format(milli / 1000);
/**
 * Netto, Steuer und Restwert eines Inventarstücks. Die Abschreibung ist linear über die Nutzungsdauer, monatsgenau ab dem Anschaffungsmonat (§ 7 Abs. 1 EStG, zeitanteilig),
 * gerechnet vom Nettobetrag (Vorsteuerabzug vorausgesetzt). Ohne Betrag, Steuersatz oder Anschaffungsdatum und Nutzungsdauer bleiben die abhängigen Werte leer.
 */
export function assetValue(input) {
    if (input.grossCents === null || input.taxRate === null)
        return { netCents: null, taxCents: null, depreciatedCents: null, bookValueCents: null };
    const netCents = Math.round((input.grossCents * 100) / (100 + input.taxRate));
    const taxCents = input.grossCents - netCents;
    if (!input.acquiredOn || !input.years)
        return { netCents, taxCents, depreciatedCents: null, bookValueCents: null };
    const [y1, m1] = input.acquiredOn.split('-').map(Number);
    const [y2, m2] = input.asOf.split('-').map(Number);
    const elapsed = (y2 - y1) * 12 + (m2 - m1) + 1;
    const total = input.years * 12;
    const used = Math.max(0, Math.min(elapsed, total));
    const depreciatedCents = Math.round((netCents * used) / total);
    return { netCents, taxCents, depreciatedCents, bookValueCents: netCents - depreciatedCents };
}
// ---------------------------------------------------------------------------
// Anlagenspiegel
// ---------------------------------------------------------------------------
/** Anlagenspiegel zum Stichtag: Inventar und (mit dem Modul Flotte) Fahrzeuge mit Anschaffung, Abschreibung und Restwert */
export const assetRegisterQuery = z.object({
    /** Stichtag, zum Beispiel der 31.12. Vorgabe: heute */
    asOf: z.iso.date().optional(),
    /** Ausgemusterte, stillgelegte und verkaufte Stücke mit aufführen */
    retired: z.enum(['1']).optional(),
});
//# sourceMappingURL=inventory.js.map